// Global client-side error capture for the SPA. This is an adapter-static,
// ssr=false app, so the SvelteKit *client* hook is the right seam — there is no
// server runtime and no hooks.server.ts need.
//
// Two layers, both routing through @cyborg7/observability/web (no OTel; a beacon
// to the configured relay's /api/cyborg/client-log, see client-telemetry-relay.ts):
//   1. installGlobalErrorHandlers() — window.onerror + unhandledrejection.
//   2. handleError — SvelteKit's hook for errors thrown during navigation /
//      load / render that don't surface as a window error.
import type { HandleClientError } from "@sveltejs/kit";
// First runtime import: the build relay reaches telemetry before anything below can report.
// oxlint-disable-next-line eslint-plugin-import/no-unassigned-import -- the side effect IS the import
import "$lib/client-telemetry-relay.js";
import { navigating } from "$app/state";
import {
  configureClientTelemetry,
  installGlobalErrorHandlers,
  reportClientError,
} from "@cyborg7/observability/web";
import { installInertFileDropGuard } from "$lib/components/composer/pane-drop.svelte.js";
import { appVersion } from "$lib/app-version.js";
import { untrack } from "svelte";
import { uid } from "$lib/tasks/uid.js";
import { workspaceState } from "$lib/core/state.svelte.js";
import { isStaleChunkError, recoverStaleChunk } from "$lib/stale-chunk.js";
import { reportUiFailure } from "$lib/report-failure.js";

// Wire window.onerror + unhandledrejection at module load (idempotent; no-op on
// non-browser hosts). +layout.svelte's onMount calls this again as a
// belt-and-suspenders safety net.
// Every report from here on carries the bundle's release version.
const version = appVersion();

const INSTALLATION_ID_KEY = "cyborg7-installation-id";
const pageLoadId = uid();
let memoryInstallationId: string | undefined;

// A uuid that outlives the page; in-memory when storage is blocked.
function installationId(): string {
  try {
    const saved = localStorage.getItem(INSTALLATION_ID_KEY);
    if (saved) return saved;
    const fresh = uid();
    localStorage.setItem(INSTALLATION_ID_KEY, fresh);
    return fresh;
  } catch {
    // intentional: storage blocked (private mode, tests); fall back to this load's id
    memoryInstallationId ??= uid();
    return memoryInstallationId;
  }
}

configureClientTelemetry({
  version,
  // untrack: a report fired inside an effect must not subscribe it to the workspace.
  ambient: () =>
    untrack(() => ({
      workspaceId: workspaceState.current?.id,
      installation_id: installationId(),
      page_load_id: pageLoadId,
      app_version: version,
    })),
});
installGlobalErrorHandlers({ version });

// A file dropped anywhere OUTSIDE a real drop target must be a no-op. Without
// this the browser navigates the tab to `file:///…` and every unsent draft in
// the composer goes with it. Bubble-phase and non-stopping, so the conversation
// panes, tasks board, calendar, attachments widget, pages list and channel
// sidebar all still receive and handle their own drops first.
installInertFileDropGuard();

export const handleError: HandleClientError = ({ error, message, status }) => {
  const err = error instanceof Error ? error : undefined;
  const text = err?.message ?? message ?? "Unknown client error";
  if (isStaleChunkError(text)) {
    // A deploy replaced the chunk: with no navigation in flight, a hover preload nobody saw.
    reportUiFailure(
      "sveltekit-client.stale_chunk",
      err ?? new Error(text),
      {},
      { failureKind: recoverStaleChunk(navigating.to?.url) },
    );
    return { message: "Something went wrong. Please try again.", staleChunk: true };
  }
  // No route matches the address (a crawler's probe, a hand-typed or old link): an answer, not
  // a fault. The path stays out of the message, so one condition is one issue.
  if (status === 404) {
    reportUiFailure(
      "sveltekit-client.not_found",
      new Error("no route matches the address"),
      {},
      {
        code: "not_found",
      },
    );
    return { message: "Something went wrong. Please try again." };
  }
  reportClientError({
    source: "sveltekit-client",
    message: text,
    stack: err?.stack ?? null,
  });

  // Safe, user-facing message — never leak internals.
  return { message: "Something went wrong. Please try again." };
};
